/**
 * Which screen a person was on when they hit the button.
 *
 * This is the whole reason the launcher sits in the header rather than on
 * each page. Nobody writes "I was on the collab list, the one at
 * /planner/collab-list" into a free-text box; they write "it's broken".
 * Capturing the route costs the submitter nothing and gives the board the
 * one field it needs to group by surface.
 *
 * Two deliberate limits on what gets recorded:
 *
 *   - The query string is dropped. Planner filters carry brand and creator
 *     names, and a feedback tracker is the wrong place for them. Anyone who
 *     needs the exact URL can paste it into the description themselves.
 *   - Path segments that are ids or names are replaced with a placeholder,
 *     so a thousand collab lists group as one screen instead of a thousand.
 *
 * Pure. Takes a pathname string, returns plain objects.
 */

/**
 * Ordered: the first match wins, so a longer path sits above the shorter
 * one it starts with. `/planner/invoices/workspace` before
 * `/planner/invoices`, or the workspace never names itself.
 */
const SCREENS = [
  { id: 'invoice-workspace', label: 'Invoices — campaign workspace', match: /^\/planner\/invoices\/workspace/ },
  { id: 'invoices', label: 'Invoices — console', match: /^\/planner\/invoices/ },
  { id: 'creator-invoices', label: 'Creator — my invoices', match: /^\/influencer\/invoices/ },
  { id: 'payout-settings', label: 'Creator — payout settings', match: /^\/influencer\/payout-settings/ },
  { id: 'unique-reach', label: 'Unique Reach', match: /^\/planner\/unique-reach/ },
  { id: 'collab-list', label: 'Collab List', match: /^\/planner\/collab-list/ },
  { id: 'curated-list', label: 'Curated List', match: /^\/planner\/curated-list/ },
  { id: 'campaign', label: 'Campaign', match: /^\/planner\/campaign/ },
  { id: 'creator-insight', label: 'Creator insight', match: /^\/planner\/(insight|profile-details|profile)\b/ },
  { id: 'competitor-report', label: 'Competitor report', match: /^(\/planner)?\/competitor-report/ },
  { id: 'competitor-insights', label: 'Competitor insights', match: /^\/competitor-insights/ },
  { id: 'discover', label: 'Discover', match: /^\/planner\/discover/ },
  { id: 'help', label: 'Help and support', match: /^\/planner\/help-and-support/ },
  { id: 'feedback', label: 'Feedback', match: /^(\/planner\/feedback|\/feedbackboard)/ },
  { id: 'brand', label: 'Brand area', match: /^\/brand/ },
  { id: 'creator-area', label: 'Creator area', match: /^\/influencer/ },
  { id: 'discovery', label: 'Discovery', match: /^\/discovery/ },
  { id: 'ai-chat', label: 'AI chat', match: /^\/ai-chat/ },
];

export const UNKNOWN = { id: 'other', label: 'Somewhere else' };

/** Every screen the board can filter by, plus the catch-all. */
export const SCREEN_OPTIONS = [...SCREENS.map(({ id, label }) => ({ id, label })), UNKNOWN];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DIGITS = /^\d+$/;

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

/** The pathname on its own: no origin, no query, no hash, no trailing slash. */
export function cleanPath(pathname) {
  if (isBlank(pathname)) return '';
  const raw = String(pathname).trim();
  const withoutOrigin = raw.replace(/^[a-z]+:\/\/[^/]+/i, '');
  const withoutQuery = withoutOrigin.split('?')[0].split('#')[0];
  const trimmed = withoutQuery.replace(/\/+$/, '');
  return trimmed || '/';
}

/**
 * A segment is an identifier if it is a uuid, all digits, or simply too
 * long and specific to be part of a route -- a collab list called
 * "Fortune Foods Diwali 2026" is data, not a page name.
 */
function placeholderFor(segment) {
  const decoded = (() => {
    try {
      return decodeURIComponent(segment);
    } catch (e) {
      return segment;
    }
  })();
  if (UUID.test(segment)) return ':id';
  if (DIGITS.test(segment)) return ':id';
  if (decoded.length > 24 || /\s/.test(decoded)) return ':name';
  return null;
}

/** `/planner/collab-list/Diwali%20Push/influencers/8f2c-...` → `/planner/collab-list/:name/influencers/:id` */
export function redactPath(pathname) {
  const path = cleanPath(pathname);
  if (!path || path === '/') return path;
  return path
    .split('/')
    .map((segment, index) => (index === 0 ? segment : placeholderFor(segment) || segment))
    .join('/');
}

/** The screen a path belongs to. Always returns something. */
export function screenFor(pathname) {
  const path = cleanPath(pathname);
  const found = SCREENS.find((screen) => screen.match.test(path));
  return found ? { id: found.id, label: found.label } : { ...UNKNOWN };
}

/** The readable name for a stored screen id, for rows that only kept the id. */
export function screenLabel(id) {
  if (isBlank(id)) return UNKNOWN.label;
  const found = SCREENS.find((screen) => screen.id === id);
  return found ? found.label : UNKNOWN.label;
}
